import React, { useState } from 'react';
import { useOpcionesCapacidad, claveCanal, nombreCanal } from '../hooks/useOpcionesCapacidad';
import MultiSelectCanales from './MultiSelectCanales';
import { textMatch } from '../../../shared/utils/textUtils';
import styles from './estudios-canales-horario.module.css';

// Sección de un horario de técnico: cada tipo de estudio marcado lleva uno o más canales de
// equipo y cuántos turnos por día hace (cada turno usa un equipo de alguno de esos canales).
// estudios: [{ idTipoEstudio, maxTurnosDiarios, canales: [{ canales, conCpap }] }]
const EstudiosCanalesHorario = ({ idCentro, estudios, onChange }) => {
  const { tiposEstudios, canalesPorEstudio, opcionesCanales, loadingCanales, error } = useOpcionesCapacidad(true, idCentro);
  const [buscar, setBuscar] = useState('');
  const [soloMarcados, setSoloMarcados] = useState(false);

  const sedeConEquipos = opcionesCanales.length > 0;
  const estudioSel = (id) => estudios.find(e => e.idTipoEstudio === id);

  // Canales de la sede que sirven para el estudio (si el estudio no tiene canales configurados, todos).
  const canalesDe = (idTipoEstudio) => {
    const habilitados = canalesPorEstudio[idTipoEstudio];
    if (!habilitados || habilitados.length === 0) return opcionesCanales;
    const claves = habilitados.map(claveCanal);
    return opcionesCanales.filter(o => claves.includes(claveCanal(o)));
  };

  const actualizar = (id, cambios) => onChange(estudios.map(e => e.idTipoEstudio === id ? { ...e, ...cambios } : e));

  const toggleEstudio = (id) => {
    if (estudioSel(id)) {
      onChange(estudios.filter(e => e.idTipoEstudio !== id));
      return;
    }
    // Si hay un solo canal posible, se elige solo.
    const posibles = canalesDe(id);
    onChange([...estudios, {
      idTipoEstudio: id,
      maxTurnosDiarios: 1,
      canales: posibles.length === 1 ? [{ canales: posibles[0].canales, conCpap: posibles[0].conCpap }] : [],
    }]);
  };

  const cambiarCanales = (id, claves) => actualizar(id, {
    canales: opcionesCanales
      .filter(o => claves.includes(claveCanal(o)))
      .map(o => ({ canales: o.canales, conCpap: o.conCpap })),
  });

  const parsear = (v) => (v === '' ? '' : Math.max(1, parseInt(v, 10) || 1));

  const totalTurnos = estudios.reduce((s, e) => s + (Number(e.maxTurnosDiarios) || 0), 0);
  const nombreEstudio = (id) => tiposEstudios.find(t => t.idTipoEstudio === id)?.tipoEstudio || `Estudio ${id}`;

  // Estudios del horario que eligieron cada canal (para la tabla de equipos).
  const estudiosPorCanal = (op) => estudios
    .filter(e => (e.canales || []).some(c => claveCanal(c) === claveCanal(op)))
    .map(e => `${nombreEstudio(e.idTipoEstudio)} (${e.maxTurnosDiarios || 0})`);

  const visibles = tiposEstudios.filter(te =>
    (!soloMarcados || estudioSel(te.idTipoEstudio)) &&
    textMatch(te.tipoEstudio, buscar)
  );

  return (
    <div className={styles['capacidad-horario']}>
      {error && <p className={styles['capacidad-error']} role="alert">{error}</p>}

      <fieldset className={styles['capacidad-panel']}>
        <legend className={styles['panel-titulo']}>
          <span>* Estudios que hace en este horario</span>
          <span className={styles['panel-subtitulo']}>{estudios.length} marcados · {totalTurnos} turnos por día</span>
        </legend>

        <div className={styles['panel-ayuda']}>
          Marcá cada estudio, elegí con qué canales de equipo se puede hacer y cuántos turnos por día toma.
          Cada turno usa un equipo.
        </div>

        <div className={styles['panel-filtros']}>
          <input
            type="search"
            className={styles['panel-buscar']}
            placeholder="Buscar estudio..."
            aria-label="Buscar estudio"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
          <label htmlFor="horario-solo-marcados" className={styles['panel-check']}>
            <input
              id="horario-solo-marcados"
              type="checkbox"
              checked={soloMarcados}
              onChange={(e) => setSoloMarcados(e.target.checked)}
            />
            Ver solo los marcados
          </label>
        </div>

        <div className={styles['panel-encabezado']} aria-hidden="true">
          <span>Estudio</span>
          {sedeConEquipos && <span className={styles['col-canales']}>Canales del equipo</span>}
          <span className={styles['col-turnos']}>Turnos por día</span>
        </div>

        <div className={styles['panel-lista']}>
          {tiposEstudios.length === 0 && <p className={styles['panel-vacio']}>Cargando tipos de estudio...</p>}
          {!idCentro && <p className={styles['panel-vacio']}>Elegí un centro para ver los canales de sus equipos.</p>}
          {idCentro && !loadingCanales && !sedeConEquipos && (
            <p className={styles['panel-vacio']}>La sede no tiene equipos cargados: los estudios se guardan sin canal.</p>
          )}
          {tiposEstudios.length > 0 && visibles.length === 0 && (
            <p className={styles['panel-vacio']}>{soloMarcados ? 'Todavía no marcaste estudios.' : 'No hay estudios que coincidan con la búsqueda.'}</p>
          )}
          {visibles.map(te => {
            const sel = estudioSel(te.idTipoEstudio);
            const idCheck = `horario-estudio-${te.idTipoEstudio}`;
            const sinCanal = !!sel && sedeConEquipos && (sel.canales || []).length === 0;
            return (
              <div key={te.idTipoEstudio} className={`${styles['panel-fila']} ${sel ? styles['fila-marcada'] : ''}`}>
                <label htmlFor={idCheck} className={styles['fila-label']}>
                  <input id={idCheck} type="checkbox" checked={!!sel} onChange={() => toggleEstudio(te.idTipoEstudio)} />
                  <span>{te.tipoEstudio}</span>
                </label>
                {sel && (
                  <div className={styles['fila-controles']}>
                    {sedeConEquipos && (
                      <MultiSelectCanales
                        id={`horario-canales-${te.idTipoEstudio}`}
                        nombre={te.tipoEstudio}
                        opciones={canalesDe(te.idTipoEstudio)}
                        seleccionados={(sel.canales || []).map(claveCanal)}
                        onChange={(claves) => cambiarCanales(te.idTipoEstudio, claves)}
                        invalido={sinCanal}
                      />
                    )}
                    <input
                      type="number"
                      min={1}
                      className={styles['fila-cantidad']}
                      aria-label={`Turnos por día de ${te.tipoEstudio}`}
                      value={sel.maxTurnosDiarios}
                      onChange={(e) => actualizar(te.idTipoEstudio, { maxTurnosDiarios: parsear(e.target.value) })}
                    />
                  </div>
                )}
                {sinCanal && <span className={styles['fila-aviso']}>Elegí al menos un canal</span>}
              </div>
            );
          })}
        </div>
      </fieldset>

      {sedeConEquipos && (
        <div className={styles['equipos-sede']}>
          <h4 className={styles['equipos-titulo']}>Equipos de la sede</h4>
          <table className={styles['equipos-tabla']}>
            <thead>
              <tr>
                <th scope="col">Canal</th>
                <th scope="col">Equipos</th>
                <th scope="col">Lo usan en este horario (turnos)</th>
              </tr>
            </thead>
            <tbody>
              {opcionesCanales.map(o => {
                const usan = estudiosPorCanal(o);
                return (
                  <tr key={claveCanal(o)} className={usan.length > 0 ? styles['equipo-en-uso'] : undefined}>
                    <td>{nombreCanal(o)}</td>
                    <td>{o.equipos}</td>
                    <td>{usan.length > 0 ? usan.join(', ') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className={styles['equipos-nota']}>
            Este horario necesita {totalTurnos} {totalTurnos === 1 ? 'equipo' : 'equipos'} por día.
            Al guardar se verifica que alcancen, sumando los horarios de los demás técnicos de ese día.
          </p>
        </div>
      )}
    </div>
  );
};

export default EstudiosCanalesHorario;
